import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import marketplaceService from '../../../services/marketplaceService';
import EscrowPaymentModal from './EscrowPaymentModal';
import SellerRatingsPanel from './SellerRatingsPanel';
import TrustBadge from '../../../components/TrustBadge';
import StarRating from '../../../components/StarRating';
import ratingsService from '../../../services/ratingsService';

const ListingDetailModal = ({ listing, onClose, onOfferSubmitted }) => {
  const { user, userProfile } = useAuth();
  const [showOfferForm, setShowOfferForm] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [offerAmount, setOfferAmount] = useState('');
  const [offerMessage, setOfferMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [acceptedOffer, setAcceptedOffer] = useState(null);
  const [showRatingsPanel, setShowRatingsPanel] = useState(false);
  const [sellerBadges, setSellerBadges] = useState([]);

  const isOwnListing = listing?.seller_id === user?.id;

  React.useEffect(() => {
    if (listing?.seller_id) {
      ratingsService?.getUserBadges(listing?.seller_id)?.then(({ data }) => {
        setSellerBadges(data || []);
      });
    }
  }, [listing?.seller_id]);

  const handleSubmitOffer = async (e) => {
    e?.preventDefault();
    setSubmitting(true);

    const offerData = {
      listing_id: listing?.id,
      buyer_id: user?.id,
      seller_id: listing?.seller_id,
      offer_amount: parseFloat(offerAmount),
      currency: listing?.currency || 'USD',
      message: offerMessage
    };

    const { data, error } = await marketplaceService?.createOffer(offerData);
    
    if (!error) {
      // If offer matches asking price or seller auto-accepts, proceed to payment
      if (parseFloat(offerAmount) >= parseFloat(listing?.asking_price)) {
        setAcceptedOffer(data);
        setShowPaymentModal(true);
      } else {
        alert('Offer submitted successfully! The seller will review your offer.');
        onOfferSubmitted?.();
      }
    } else {
      alert('Failed to submit offer: ' + error);
    }
    
    setSubmitting(false);
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-card border border-border rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="Package" size={20} className="text-primary" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-card-foreground">Listing Details</h2>
                <p className="text-sm text-muted-foreground">View collectible information</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <Icon name="X" size={20} />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Image */}
              <div className="aspect-square bg-muted rounded-lg overflow-hidden">
                {listing?.featured_image_url || listing?.collectible?.primary_image_url ? (
                  <img
                    src={listing?.featured_image_url || listing?.collectible?.primary_image_url}
                    alt={listing?.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Icon name="Image" size={64} className="text-muted-foreground" />
                  </div>
                )}
              </div>

              {/* Details */}
              <div className="space-y-6">
                <div>
                  <h1 className="text-2xl font-bold text-foreground mb-2">{listing?.title}</h1>
                  <p className="text-muted-foreground">{listing?.description}</p>
                </div>

                {/* Price */}
                <div className="bg-primary/10 rounded-lg p-4">
                  <p className="text-sm text-muted-foreground mb-1">Asking Price</p>
                  <p className="text-3xl font-bold text-primary">
                    ${parseFloat(listing?.asking_price || 0)?.toFixed(2)}
                  </p>
                  {listing?.shipping_cost > 0 && (
                    <p className="text-sm text-muted-foreground mt-1">
                      +${parseFloat(listing?.shipping_cost)?.toFixed(2)} shipping
                    </p>
                  )}
                  {listing?.is_negotiable && (
                    <div className="flex items-center space-x-2 mt-2">
                      <Icon name="MessageSquare" size={14} className="text-primary" />
                      <span className="text-sm text-primary font-medium">Price is negotiable</span>
                    </div>
                  )}
                </div>

                {/* Collectible Info */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Condition</span>
                    <span className="font-medium text-foreground capitalize">
                      {listing?.condition?.replace('-', ' ')}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Type</span>
                    <span className="font-medium text-foreground capitalize">
                      {listing?.collectible?.collectible_type?.replace('-', ' ')}
                    </span>
                  </div>
                  {listing?.collectible?.brand && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Brand</span>
                      <span className="font-medium text-foreground">{listing?.collectible?.brand}</span>
                    </div>
                  )}
                  {listing?.collectible?.year && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Year</span>
                      <span className="font-medium text-foreground">{listing?.collectible?.year}</span>
                    </div>
                  )}
                </div>

                {/* Seller Info */}
                <div className="pt-4 border-t border-border">
                  <p className="text-sm text-muted-foreground mb-2">Seller</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                        <Icon name="User" size={20} className="text-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-foreground">{listing?.seller?.full_name}</p>
                        {listing?.seller_reputation?.[0] && (
                          <div className="flex items-center space-x-2 mt-1">
                            <StarRating
                              rating={listing?.seller_reputation?.[0]?.seller_rating || 0}
                              size={12}
                              showValue={true}
                              reviewCount={listing?.seller_reputation?.[0]?.total_seller_reviews}
                            />
                          </div>
                        )}
                        {sellerBadges?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {sellerBadges?.slice(0, 3)?.map(badge => (
                              <TrustBadge key={badge?.id} badgeType={badge?.badge_type} size="xs" />
                            ))}
                            {sellerBadges?.length > 3 && (
                              <span className="text-xs text-muted-foreground">+{sellerBadges?.length - 3}</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => setShowRatingsPanel(true)}
                      className="text-xs text-primary hover:text-primary/80 transition-colors flex items-center space-x-1"
                    >
                      <Icon name="Star" size={12} />
                      <span>View Ratings</span>
                    </button>
                  </div>
                </div>

                {/* Actions */}
                {!isOwnListing && (
                  <div className="pt-4 border-t border-border space-y-3">
                    {!showOfferForm ? (
                      <Button
                        onClick={() => setShowOfferForm(true)}
                        iconName="MessageSquare"
                        iconPosition="left"
                        fullWidth
                        size="lg"
                      >
                        Make an Offer
                      </Button>
                    ) : (
                      <form onSubmit={handleSubmitOffer} className="space-y-3">
                        <Input
                          label="Your Offer"
                          type="number"
                          step="0.01"
                          required
                          value={offerAmount}
                          onChange={(e) => setOfferAmount(e?.target?.value)}
                          placeholder="Enter your offer amount"
                        />
                        <div>
                          <label className="text-sm font-medium text-foreground mb-2 block">
                            Message (Optional)
                          </label>
                          <textarea
                            value={offerMessage}
                            onChange={(e) => setOfferMessage(e?.target?.value)}
                            placeholder="Add a message to the seller"
                            rows={3}
                            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                          />
                        </div>
                        <div className="flex space-x-3">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setShowOfferForm(false)}
                            fullWidth
                          >
                            Cancel
                          </Button>
                          <Button
                            type="submit"
                            loading={submitting}
                            disabled={submitting}
                            iconName="Send"
                            iconPosition="left"
                            fullWidth
                          >
                            Submit Offer
                          </Button>
                        </div>
                      </form>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Payment Modal */}
      {showPaymentModal && acceptedOffer && (
        <EscrowPaymentModal
          offer={acceptedOffer}
          listing={listing}
          onClose={() => setShowPaymentModal(false)}
          onSuccess={() => {
            setShowPaymentModal(false);
            onOfferSubmitted?.();
            onClose?.();
          }}
          onPaymentComplete={() => {
            setShowPaymentModal(false);
            onOfferSubmitted?.();
            onClose?.();
          }}
        />
      )}

      {showRatingsPanel && (
        <SellerRatingsPanel
          userId={listing?.seller_id}
          userName={listing?.seller?.full_name}
          onClose={() => setShowRatingsPanel(false)}
        />
      )}
    </>
  );
};

export default ListingDetailModal;
